<template>
  <section class="my-4">
    <div class="row border-bottom pb-4 mb-5 row">
      <section class="col-6">
        <section class="mt-4">
          <h3>{{ $t('PRODUCTS.TEXT.PRICE_OF_THE_PRODUCT') }}</h3>
          <HMRadio
            class="mt-4"
            group="price-of-the-product"
            name="fixed"
            v-model="pricingType"
          >
            {{ $t('PRODUCTS.TEXT.FIXED_PRICE_PRODUCT') }}
          </HMRadio>
          <HMRadio
            class="mt-4"
            group="price-of-the-product"
            name="recurring"
            v-model="pricingType"
          >
            {{ $t('PRODUCTS.TEXT.RECURRING_PAYMENTS') }}
          </HMRadio>
          <div v-if="isPricingTypeFixed">
            <div
              class="d-flex flex-row justify-content-start align-items-center flex-nowrap"
            >
              <HMTextInput
                :label="$t('PRODUCTS.TEXT.ONE_TIME_PAYMENT')"
                v-model="price"
                rules="required"
                class="mt-4"
                :name="$t('PRODUCTS.TEXT.PRICE_OF_THE_PRODUCT')"
              >
                <template slot="prepend">{{ getCurrencySymbol }}</template>
              </HMTextInput>
            </div>
          </div>
          <div
            v-if="isPricingTypeRecurring"
            class="d-flex align-items-start mt-4"
          >
            <div class="form-group mr-2">
              <label>{{ $t('PRODUCTS.LABELS.INTERVAL') }}</label>
              <select v-model="recurringType" class="form-control" required>
                <option
                  v-for="option in recurringTypes"
                  :key="option.name"
                  :value="option.name"
                >
                  {{ $t(option.translation) }}
                </option>
              </select>
            </div>
            <HMTextInput
              :label="$t('PRODUCTS.LABELS.PRICE')"
              v-model="price"
              rules="required"
              :name="$t('PRODUCTS.TEXT.PRICE_OF_THE_PRODUCT')"
              class="flex-grow-1"
            >
              <template slot="prepend">{{ getCurrencySymbol }}</template>
            </HMTextInput>
            <div class="ml-2 form-group flex-grow-1">
              <label>
                {{ $t('PRODUCTS.LABELS.RECURRING_STRATEGY') }}
                <span
                  v-tooltip="{
                    content: $t(
                      'PRODUCTS.TEXT.RECURRING_STRATEGY_INFO_CLASSCARD'
                    ),
                    delay: 100,
                    placement: 'top',
                    container: 'body',
                    trigger: 'click hover',
                  }"
                >
                  <i class="fa fa-info-circle ml-1"></i>
                </span>
              </label>
              <v-select
                :options="recurringStrategies"
                v-model="productForm.recurring_strategy"
                :reduce="strategy => strategy.code"
                :clearable="false"
              />
            </div>
          </div>
        </section>
      </section>
    </div>
    <div class="row border-bottom pb-4 mb-5 row">
      <section v-if="teacherTaxRates" class="col-12">
        <label>{{ $t('PRODUCTS.LABELS.TAX') }}</label>
        <v-select
          :options="mappedTeacherTaxRates"
          label="name"
          code="id"
          v-model="productForm.tax_rate"
          :reduce="taxRate => taxRate.id"
        />
      </section>
    </div>
    <div class="border-bottom pb-4 mb-5 row">
      <div class="col-12 mb-3">
        <h2>{{ $t('PRODUCTS.TEXT.PAY_WHAT_YOU_WISH') }}</h2>
      </div>
      <div class="col col-md-2 py-1 d-flex align-items-center">
        <HMCheckBox class="my-2" v-model="wishPriceEnabled">
          {{ $t('PRODUCTS.LABELS.ALLOW') }}
        </HMCheckBox>
      </div>
      <div class="col d-flex align-items-center" v-if="wishPriceEnabled">
        <HMTextInput
          class="m-0"
          v-if="wishPriceEnabled"
          v-model="wishPrice"
          rules="required|min_value:1"
          :placeholder="$t('PRODUCTS.LABELS.MIN_PAYMENT')"
          :name="$t('PRODUCTS.LABELS.MIN_PAYMENT')"
        />
      </div>
    </div>
  </section>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep, set, take } from 'lodash';

export default Vue.extend({
  name: 'ProductClassCardPricing',
  computed: {
    ...mapGetters({
      productForm: 'product/productForm',
      teacherTaxRates: 'product/teacherTaxRates',
    }),
    recurringStrategies() {
      return [
        // {
        //   code: 'fixed',
        //   label: this.$t('PRODUCTS.LABELS.RECURRING_STRATEGY_FIXED'),
        // },
        {
          code: 'add',
          label: this.$t('PRODUCTS.LABELS.RECURRING_STRATEGY_ADD'),
        },
        {
          code: 'topup',
          label: this.$t('PRODUCTS.LABELS.RECURRING_STRATEGY_TOPUP'),
        },
      ];
    },
    price: {
      get() {
        return this.productForm.price;
      },
      set(value) {
        this.$store.dispatch('product/setProductForm', {
          price: value,
        });
      },
    },
    pricingType: {
      get() {
        if (this.productForm.pricing_type) return this.productForm.pricing_type;
        return 'fixed';
      },
      set(value) {
        this.$store.dispatch('product/setProductForm', {
          pricing_type: value,
          price: null,
          day_pass_enabled: false,
          day_pass_price: null,
          trial: false,
          trialPeriod: null,
          trialUnits: null,
          trial_period_selected: null,
          recurring_strategy: value === 'fixed' ? null : 'add',
        });
        if (this.isPricingTypeRecurring) {
          this.recurringType = this.productForm.recurring_type ?? 'monthly';
        } else {
          this.recurringType = null;
        }
      },
    },
    isPricingTypeFixed() {
      return this.productForm.pricing_type === 'fixed';
    },
    isPricingTypeRecurring() {
      return this.productForm.pricing_type === 'recurring';
    },
    isRecurringTypeMultiple() {
      return this.productForm.recurring_type === 'multiple';
    },
    recurringOptionsList() {
      return take(this.recurringTypes, this.recurringTypes.length - 1);
    },
    recurringType: {
      get() {
        return this.productForm.recurring_type ?? 'monthly';
      },
      set(value) {
        this.$store.dispatch('product/setProductForm', {
          recurring_type: value,
        });
      },
    },
    getCurrencySymbol() {
      return this.productForm.currency;
    },
    recurringOptions: {
      get() {
        const defaultOption = () => {
          return {
            available: false,
            price: '',
            min_wish_price: '',
            number_of_punches: '',
            number_of_included_sessions: '',
          };
        };

        return {
          annually: defaultOption(),
          biannually: defaultOption(),
          daily: defaultOption(),
          monthly: defaultOption(),
          quarterly: defaultOption(),
          weekly: defaultOption(),
          ...this.productForm.recurring_options,
        };
      },
      set(value) {
        this.$store.dispatch('product/setProductForm', {
          recurring_options: value,
        });
      },
    },
    mappedTeacherTaxRates() {
      if (!this.teacherTaxRates) return [];
      return this.teacherTaxRates
        .filter(taxRate => taxRate.active)
        .map(taxRate => {
          return {
            ...taxRate,
            name: `${taxRate.name} (${taxRate.rate}%)`,
          };
        });
    },
    wishPriceEnabled: {
      get() {
        return this.productForm.wish_price_enabled;
      },
      set(value) {
        this.$store.dispatch('product/setProductForm', {
          wish_price_enabled: value,
        });
      },
    },
    wishPrice: {
      get() {
        return this.productForm.min_wish_price;
      },
      set(value) {
        this.$store.dispatch('product/setProductForm', {
          min_wish_price: value || null,
        });
      },
    },
    recurringTypes() {
      return [
        {
          name: 'annually',
          translation: 'GENERAL.RECURRING_TYPE.ANNUALLY',
          wishTranslation: 'GENERAL.MIN_RECURRING_TYPE_PAYMENT.ANNUALLY',
        },
        {
          name: 'biannually',
          translation: 'GENERAL.RECURRING_TYPE.BIANNUALLY',
          wishTranslation: 'GENERAL.MIN_RECURRING_TYPE_PAYMENT.BIANNUALLY',
        },
        {
          name: 'quarterly',
          translation: 'GENERAL.RECURRING_TYPE.QUARTERLY',
          wishTranslation: 'GENERAL.MIN_RECURRING_TYPE_PAYMENT.QUARTERLY',
        },
        {
          name: 'monthly',
          translation: 'GENERAL.RECURRING_TYPE.MONTHLY',
          wishTranslation: 'GENERAL.MIN_RECURRING_TYPE_PAYMENT.MONTHLY',
        },
        {
          name: 'weekly',
          translation: 'GENERAL.RECURRING_TYPE.WEEKLY',
          wishTranslation: 'GENERAL.MIN_RECURRING_TYPE_PAYMENT.WEEKLY',
        },
        {
          name: 'daily',
          translation: 'GENERAL.RECURRING_TYPE.DAILY',
          wishTranslation: 'GENERAL.MIN_RECURRING_TYPE_PAYMENT.DAILY',
        },
      ];
    },
    periodTypes() {
      return [
        {
          name: 'D',
          translation: 'GENERAL.PERIOD.DAYS',
        },
        {
          name: 'W',
          translation: 'GENERAL.PERIOD.WEEKS',
        },
        {
          name: 'M',
          translation: 'GENERAL.PERIOD.MONTHS',
        },
      ];
    },
  },
  methods: {
    setDefaults() {
      if (
        !this.productForm.pricing_type ||
        this.productForm?.pricing_type === 'free'
      ) {
        this.$store.dispatch('product/setProductForm', {
          pricing_type: 'fixed',
        });
      }
    },
    handleRecurrentOption(identifier, value) {
      const recurringOptions = cloneDeep(this.recurringOptions);
      set(recurringOptions, `${identifier}.price`, value);
      set(recurringOptions, `${identifier}.available`, value > 0);
      this.recurringOptions = recurringOptions;
    },
  },
  watch: {
    productForm: {
      handler(productForm) {
        if (!productForm) return;
        this.setDefaults();
      },
      immediate: true,
    },
  },
});
</script>

<style lang="scss" scoped></style>
